import type { Meta, PlanShard, PublishedPlan, TariffFile, ValidationReport, ZipIndex } from "./types";

const BASE = `${import.meta.env.BASE_URL}data`;

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}/${path}`);
  if (!response.ok) {
    throw new Error(`Could not load ${path} (HTTP ${response.status})`);
  }
  return (await response.json()) as T;
}

// Shards are immutable per ingest run, so a plain in-memory cache is enough to
// keep re-entering a ZIP or changing usage from re-downloading them.
const shardCache = new Map<string, Promise<PlanShard>>();

export const loadZipIndex = once(() => getJson<ZipIndex>("zip-index.json"));
export const loadTariffs = once(() => getJson<TariffFile>("tdu-tariffs.json"));
export const loadMeta = once(() => getJson<Meta>("meta.json"));
export const loadValidationReport = once(() => getJson<ValidationReport>("validation-report.json"));

export function loadShard(tdu: string): Promise<PlanShard> {
  let pending = shardCache.get(tdu);
  if (!pending) {
    pending = getJson<PlanShard>(`plans/${tdu}.json`);
    shardCache.set(tdu, pending);
  }
  return pending;
}

/**
 * Plans available in one ZIP.
 *
 * The index maps a ZIP to an availability group, which names the utilities
 * serving it and the exact plan ids offered. Only that group's shards are
 * fetched, so a Houston visitor never downloads the Lubbock catalog.
 */
export async function loadPlansForZip(zip: string): Promise<{
  plans: PublishedPlan[];
  tdus: string[];
  generatedAt: string;
} | null> {
  const index = await loadZipIndex();
  const groupId = index.zips[zip];
  if (groupId === undefined) return null;

  const group = index.groups.find((g) => g.id === groupId);
  if (!group) return null;

  const shards = await Promise.all(group.tdus.map(loadShard));
  const allowed = new Set(group.plan_ids);
  const plans = shards
    .flatMap((shard) => shard.plans)
    .filter((plan) => allowed.has(plan.plan_id));

  return {
    plans,
    tdus: group.tdus,
    generatedAt: shards[0]?.generated_at ?? index.generated_at,
  };
}

function once<T>(fn: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | undefined;
  return () => (pending ??= fn());
}
